import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  TrendingUp, TrendingDown, ShoppingCart, Package, AlertTriangle, 
  Users, Wallet, Clock, ArrowRight, BarChart3, PieChart
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, Legend, BarChart, Bar
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/pos`;

const StatCard = ({ title, value, subtitle, icon: Icon, color, bgColor, trend, trendUp, onClick }) => (
  <div 
    onClick={onClick}
    className={`bg-white rounded-xl border border-slate-200 p-4 md:p-5 hover:shadow-lg transition-all ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <p className="text-xs md:text-sm text-slate-500 mb-1 truncate">{title}</p>
        <p className={`text-xl md:text-2xl font-bold ${color}`}>{value}</p>
        {subtitle && <p className="text-xs text-slate-400 mt-1 truncate">{subtitle}</p>}
        {trend && (
          <div className={`flex items-center gap-1 mt-2 text-xs ${trendUp ? 'text-emerald-600' : 'text-red-500'}`}>
            {trendUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            <span>{trend}</span>
          </div>
        )}
      </div>
      <div className={`p-2 md:p-3 rounded-xl ${bgColor} flex-shrink-0`}>
        <Icon size={20} className={color} />
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(1); // Default to Today
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [profileRes, statsRes, subRes] = await Promise.all([
        axios.get(`${API}/profile`, { headers }),
        axios.get(`${API}/dashboard/stats?days=${selectedPeriod}`, { headers }),
        axios.get(`${API}/subscription-info`, { headers }).catch(() => ({ data: null }))
      ]);

      setProfile(profileRes.data);
      setStats(statsRes.data);
      setSubscriptionInfo(subRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toFixed(0)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-200 border-t-violet-600"></div>
      </div>
    );
  }

  const COLORS = ['#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899', '#14b8a6', '#f97316'];

  return (
    <div data-testid="pos-dashboard" className="space-y-4 md:space-y-6">
      {/* Header with Period Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Welcome, {profile?.business_name}
          </h1>
          <p className="text-slate-500 text-sm">Store ID: {profile?.store_id}</p>
        </div>
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg self-start flex-wrap">
          {[
            { days: 1, label: 'Today' },
            { days: 7, label: '7D' },
            { days: 15, label: '15D' },
            { days: 30, label: '30D' }
          ].map(({ days, label }) => (
            <button
              key={days}
              onClick={() => setSelectedPeriod(days)}
              data-testid={`period-${days}`}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                selectedPeriod === days 
                  ? 'bg-white text-violet-600 shadow-sm' 
                  : 'text-slate-600 hover:text-violet-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Top Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <StatCard
          title="Total Sales"
          value={formatCurrency(stats?.sales?.total || 0)}
          subtitle={`${stats?.sales?.total_bills || 0} bills`}
          icon={TrendingUp}
          color="text-emerald-600"
          bgColor="bg-emerald-50"
          onClick={() => navigate('/gposp/reports')}
        />
        <StatCard
          title="Total Expenses"
          value={formatCurrency(stats?.expenses?.total || 0)}
          subtitle={selectedPeriod === 1 ? 'Today' : `Last ${selectedPeriod} days`}
          icon={Wallet}
          color="text-red-500"
          bgColor="bg-red-50"
          onClick={() => navigate('/gposp/expenses')}
        />
        <StatCard
          title="Outstanding"
          value={formatCurrency(stats?.outstanding?.total || 0)}
          subtitle={`${stats?.outstanding?.total_customers || 0} customers`}
          icon={Users}
          color="text-amber-600"
          bgColor="bg-amber-50"
          onClick={() => navigate('/gposp/customer-ledger')}
        />
        <StatCard
          title="Avg Bill Value"
          value={formatCurrency(stats?.sales?.avg_bill || 0)}
          subtitle="Per transaction"
          icon={ShoppingCart}
          color="text-violet-600"
          bgColor="bg-violet-50"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Sales Chart - Takes 2 columns */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-4 md:p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="text-violet-600" size={20} />
              <h3 className="font-semibold text-slate-800">Sales Trend</h3>
            </div>
            <span className="text-sm text-slate-500">{selectedPeriod} days</span>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={stats?.sales?.data || []}>
              <defs>
                <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 11, fill: '#64748b' }}
                axisLine={{ stroke: '#e2e8f0' }}
              />
              <YAxis 
                tick={{ fontSize: 11, fill: '#64748b' }}
                axisLine={{ stroke: '#e2e8f0' }}
                tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
                formatter={(value) => [`₹${value.toLocaleString('en-IN')}`, 'Sales']}
              />
              <Area 
                type="monotone" 
                dataKey="sales" 
                stroke="#8b5cf6" 
                strokeWidth={2}
                fill="url(#salesGradient)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Expenses Pie Chart */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-5">
          <div className="flex items-center gap-2 mb-4">
            <PieChart className="text-red-500" size={20} />
            <h3 className="font-semibold text-slate-800">Expenses</h3>
          </div>
          {stats?.expenses?.data?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <RechartsPie>
                <Pie
                  data={stats.expenses.data}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="amount"
                  nameKey="category"
                >
                  {stats.expenses.data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => `₹${value.toLocaleString('en-IN')}`}
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Legend 
                  layout="vertical" 
                  align="right" 
                  verticalAlign="middle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: '11px' }}
                />
              </RechartsPie>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-slate-400 text-sm">
              No expenses recorded
            </div>
          )}
        </div>
      </div>

      {/* Second Row - Top Products, Stock Alerts, Outstanding */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Top Products */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Package className="text-emerald-600" size={20} />
              <h3 className="font-semibold text-slate-800">Top Products</h3>
            </div>
            <button 
              onClick={() => navigate('/gposp/products')}
              className="text-xs text-violet-600 hover:underline flex items-center gap-1"
            >
              View all <ArrowRight size={12} />
            </button>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {stats?.top_products?.length > 0 ? (
              stats.top_products.slice(0, 5).map((product, index) => (
                <div key={product.product_id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      index < 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {index + 1}
                    </span>
                    <span className="text-sm text-slate-700 truncate max-w-[120px]">{product.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-slate-800">₹{product.revenue?.toFixed(0)}</p>
                    <p className="text-xs text-slate-400">{product.quantity} sold</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">No sales data</p>
            )}
          </div>
        </div>

        {/* Stock Alerts */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-amber-500" size={20} />
              <h3 className="font-semibold text-slate-800">Stock Alerts</h3>
            </div>
            <button 
              onClick={() => navigate('/gposp/stock-management')}
              className="text-xs text-violet-600 hover:underline flex items-center gap-1"
            >
              Manage <ArrowRight size={12} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div 
              onClick={() => navigate('/gposp/stock-management')}
              className="p-4 bg-red-50 border border-red-100 rounded-xl cursor-pointer hover:shadow-md transition-all"
            >
              <p className="text-3xl font-bold text-red-600">{stats?.stock_alerts?.out_of_stock || 0}</p>
              <p className="text-xs text-red-700 mt-1">Out of Stock</p>
            </div>
            <div 
              onClick={() => navigate('/gposp/stock-management')}
              className="p-4 bg-amber-50 border border-amber-100 rounded-xl cursor-pointer hover:shadow-md transition-all"
            >
              <p className="text-3xl font-bold text-amber-600">{stats?.stock_alerts?.low_stock || 0}</p>
              <p className="text-xs text-amber-700 mt-1">Low Stock</p>
            </div>
          </div>
          <div className="mt-4 p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Total Active Products</span>
              <span className="font-semibold text-slate-700">{stats?.stock_alerts?.total_products || 0}</span>
            </div>
          </div>
        </div>

        {/* Outstanding Payments */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="text-blue-600" size={20} />
              <h3 className="font-semibold text-slate-800">Outstanding</h3>
            </div>
            <button 
              onClick={() => navigate('/gposp/customer-ledger')}
              className="text-xs text-violet-600 hover:underline flex items-center gap-1"
            >
              Collect <ArrowRight size={12} />
            </button>
          </div>
          <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 rounded-xl mb-4">
            <p className="text-2xl font-bold text-amber-700">₹{(stats?.outstanding?.total || 0).toLocaleString('en-IN')}</p>
            <p className="text-xs text-amber-600 mt-1">Total Outstanding</p>
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {stats?.outstanding?.customers?.slice(0, 4).map(customer => (
              <div key={customer.customer_id} className="flex items-center justify-between py-1.5 text-sm">
                <span className="text-slate-600 truncate max-w-[140px]">{customer.name}</span>
                <span className="font-medium text-amber-600">₹{customer.balance?.toFixed(0)}</span>
              </div>
            ))}
            {!stats?.outstanding?.customers?.length && (
              <p className="text-sm text-slate-400 text-center py-2">No outstanding payments</p>
            )}
          </div>
        </div>
      </div>

      {/* Pending Approvals Alert */}
      {stats?.pending_approvals > 0 && (
        <div 
          onClick={() => navigate('/gposp/payment-approvals')}
          className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-4 flex items-center justify-between cursor-pointer hover:shadow-md transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-violet-100 rounded-lg">
              <Clock className="text-violet-600" size={20} />
            </div>
            <div>
              <p className="font-semibold text-violet-800">Pending Payment Approvals</p>
              <p className="text-sm text-violet-600">{stats.pending_approvals} payment(s) awaiting your approval</p>
            </div>
          </div>
          <ArrowRight className="text-violet-600" size={20} />
        </div>
      )}

      {/* Subscription Status */}
      {subscriptionInfo && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800">Subscription & Usage</h3>
            <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-medium">
              {subscriptionInfo.plan_name}
            </span>
          </div>
          
          {/* Storage Warning Banner */}
          {(subscriptionInfo.warnings?.storage_warning || subscriptionInfo.warnings?.storage_critical) && (
            <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
              subscriptionInfo.warnings.storage_critical 
                ? 'bg-red-50 border border-red-200' 
                : 'bg-amber-50 border border-amber-200'
            }`}>
              <AlertTriangle size={16} className={subscriptionInfo.warnings.storage_critical ? 'text-red-500' : 'text-amber-500'} />
              <span className={`text-sm ${subscriptionInfo.warnings.storage_critical ? 'text-red-700' : 'text-amber-700'}`}>
                {subscriptionInfo.warnings.storage_critical 
                  ? 'Storage critical! Please upgrade your plan to continue uploading.'
                  : 'Storage above 75%. Consider upgrading your plan soon.'}
              </span>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Products Usage */}
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-slate-500">Products</span>
                <span className="text-xs font-medium text-slate-700">
                  {subscriptionInfo.usage.products.used} / {subscriptionInfo.usage.products.limit}
                </span>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    subscriptionInfo.usage.products.percent >= 90 ? 'bg-red-500' : 
                    subscriptionInfo.usage.products.percent >= 75 ? 'bg-amber-500' : 'bg-violet-500'
                  }`}
                  style={{ width: `${Math.min(subscriptionInfo.usage.products.percent, 100)}%` }}
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">{subscriptionInfo.usage.products.percent?.toFixed(0)}% used</p>
            </div>
            
            {/* Bills Usage */}
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-slate-500">Bills/Month</span>
                <span className="text-xs font-medium text-slate-700">
                  {subscriptionInfo.usage.bills_this_month.used} / {subscriptionInfo.usage.bills_this_month.limit || '∞'}
                </span>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    subscriptionInfo.usage.bills_this_month.percent >= 90 ? 'bg-red-500' : 
                    subscriptionInfo.usage.bills_this_month.percent >= 75 ? 'bg-amber-500' : 'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min(subscriptionInfo.usage.bills_this_month.percent || 0, 100)}%` }}
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">
                {subscriptionInfo.usage.bills_this_month.limit === 'Unlimited' ? 'Unlimited' : `${subscriptionInfo.usage.bills_this_month.percent?.toFixed(0) || 0}% used`}
              </p>
            </div>
            
            {/* Storage Usage - Enhanced */}
            <div className={`p-3 rounded-lg ${
              subscriptionInfo.warnings?.storage_critical ? 'bg-red-50 border border-red-200' :
              subscriptionInfo.warnings?.storage_warning ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'
            }`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-slate-500">Storage</span>
                <span className="text-xs font-medium text-slate-700">
                  {subscriptionInfo.usage.storage.display || `${subscriptionInfo.usage.storage.used_mb?.toFixed(1)} MB / ${subscriptionInfo.usage.storage.limit_mb?.toFixed(0)} MB`}
                </span>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    subscriptionInfo.usage.storage.percent >= 90 ? 'bg-red-500' : 
                    subscriptionInfo.usage.storage.percent >= 75 ? 'bg-amber-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${Math.min(subscriptionInfo.usage.storage.percent, 100)}%` }}
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">{subscriptionInfo.usage.storage.percent?.toFixed(1)}% used</p>
            </div>
          </div>
          
          {/* Subscription End Date */}
          {subscriptionInfo.subscription_end && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-sm">
              <span className="text-slate-500">Plan valid until</span>
              <span className="font-medium text-slate-700">
                {new Date(subscriptionInfo.subscription_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-5">
        <h3 className="font-semibold text-slate-800 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <button
            onClick={() => navigate('/gposp/pos')}
            data-testid="quick-action-billing"
            className="p-4 bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-xl hover:shadow-lg transition-all text-center"
          >
            <ShoppingCart className="mx-auto mb-2 text-violet-600" size={24} />
            <p className="text-sm font-medium text-violet-700">New Bill</p>
          </button>
          <button
            onClick={() => navigate('/gposp/products')}
            data-testid="quick-action-products"
            className="p-4 bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-xl hover:shadow-lg transition-all text-center"
          >
            <Package className="mx-auto mb-2 text-emerald-600" size={24} />
            <p className="text-sm font-medium text-emerald-700">Products</p>
          </button>
          <button
            onClick={() => navigate('/gposp/payment-collection')}
            data-testid="quick-action-collect"
            className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl hover:shadow-lg transition-all text-center"
          >
            <Wallet className="mx-auto mb-2 text-amber-600" size={24} />
            <p className="text-sm font-medium text-amber-700">Collect</p>
          </button>
          <button
            onClick={() => navigate('/gposp/reports')}
            data-testid="quick-action-reports"
            className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl hover:shadow-lg transition-all text-center"
          >
            <BarChart3 className="mx-auto mb-2 text-blue-600" size={24} />
            <p className="text-sm font-medium text-blue-700">Reports</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
